import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { pipelineApi, agentApi } from '../../services/api';
import { 
  FunnelIcon, 
  PlusIcon, 
  ArrowPathIcon,
  ChartBarIcon,
  UserCircleIcon,
  ChatBubbleLeftIcon,
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PIPELINE_STAGES = [
  { id: 'new', name: 'New', color: 'bg-gray-50', borderColor: 'border-gray-200', textColor: 'text-gray-700' },
  { id: 'analyzing', name: 'Analyzing', color: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-700' },
  { id: 'qualified', name: 'Qualified', color: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-700' },
  { id: 'proposal', name: 'Proposal', color: 'bg-purple-50', borderColor: 'border-purple-200', textColor: 'text-purple-700' },
  { id: 'submitted', name: 'Submitted', color: 'bg-orange-50', borderColor: 'border-orange-200', textColor: 'text-orange-700' },
  { id: 'awarded', name: 'Awarded', color: 'bg-emerald-50', borderColor: 'border-emerald-200', textColor: 'text-emerald-700' },
  { id: 'lost', name: 'Lost', color: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-700' },
  { id: 'archived', name: 'Archived', color: 'bg-slate-50', borderColor: 'border-slate-200', textColor: 'text-slate-700' },
];

interface PipelineOpportunity {
  id: string;
  opportunityId: string;
  stage: string;
  matchScore: number;
  assignedTo?: string;
  notes?: string;
  autoProcessed?: boolean;
  opportunity?: {
    id: string;
    samNoticeId: string;
    title: string;
    agency?: string;
    subAgency?: string;
    naicsCode?: string;
    naicsCodes?: string[];
    responseDeadline?: string;
    setAside?: string;
    type?: string;
    solicitationNumber?: string;
  };
  metadata?: {
    lastActivity?: string;
    comments?: number;
  };
  createdAt: string;
  updatedAt: string;
}

const Pipeline: React.FC = () => {
  const [opportunities, setOpportunities] = useState<PipelineOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [filter, setFilter] = useState({ minScore: 0, assignedToMe: false });

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await pipelineApi.getOpportunities();
      // API returns { success: true, data: { opportunities: [...], total: n } }
      const pipelineData = response.data.data?.opportunities || response.data.opportunities || [];
      setOpportunities(pipelineData);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await pipelineApi.getConfig();
      // API returns { success: true, data: { ...config } }
      setConfig(response.data.data || response.data.config);
    } catch (error) {
      console.error('Failed to load config');
    }
  };

  useEffect(() => {
    fetchPipeline();
    fetchConfig();
  }, []);

  const handleStageChange = async (oppId: string, newStage: string) => {
    try {
      await pipelineApi.updateStage(oppId, { stage: newStage });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.id === newStage)?.name}`);
      fetchPipeline();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleAnalyze = async (oppId: string) => {
    try {
      toast.loading('Analyzing opportunity...', { id: `analyze-${oppId}` });
      await pipelineApi.analyze(oppId);
      toast.success('Analysis complete', { id: `analyze-${oppId}` });
      fetchPipeline();
    } catch (error) {
      toast.error('Analysis failed', { id: `analyze-${oppId}` });
    }
  };

  const handleMatchVendors = async (oppId: string) => {
    try {
      toast.loading('Finding matching vendors...', { id: `match-${oppId}` });
      await pipelineApi.matchVendors(oppId);
      toast.success('Vendor matching complete', { id: `match-${oppId}` });
    } catch (error) {
      toast.error('Vendor matching failed', { id: `match-${oppId}` });
    }
  };

  const getStageOpportunities = (stageId: string) => {
    return opportunities
      .filter(opp => opp.stage === stageId)
      .filter(opp => opp.matchScore >= filter.minScore / 100)
      .sort((a, b) => b.matchScore - a.matchScore);
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(value);
  };

  const timeLeft = (deadline?: string) => {
    if (!deadline) return 'N/A';
    const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days === 0) return 'Due today';
    if (days === 1) return '1 day left';
    if (days <= 7) return `${days} days left`;
    return `${Math.floor(days / 7)} weeks left`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  // Render a single stage column
  const renderStageColumn = (stage: typeof PIPELINE_STAGES[0]) => {
    const stageOpps = getStageOpportunities(stage.id);
    return (
      <div
        key={stage.id}
        className={`${stage.color} rounded-lg border ${stage.borderColor} min-h-[300px] flex flex-col`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const oppId = e.dataTransfer.getData('oppId');
          if (oppId && oppId !== draggingId) {
            handleStageChange(oppId, stage.id);
          }
          setDraggingId(null);
        }}
      >
        {/* Stage Header */}
        <div className={`p-3 border-b ${stage.borderColor} flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <h3 className={`font-semibold ${stage.textColor}`}>{stage.name}</h3>
            <span className="bg-white px-2 py-1 rounded-full text-xs font-medium text-gray-600">
              {stageOpps.length}
            </span>
          </div>
        </div>

        {/* Stage Cards */}
        <div className="p-3 space-y-3 flex-1">
          {stageOpps.map(opp => (
            <div
              key={opp.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('oppId', opp.id);
                setDraggingId(opp.id);
              }}
              onDragEnd={() => setDraggingId(null)}
              className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow ${
                draggingId === opp.id ? 'opacity-50' : ''
              }`}
            >
              {/* Match Score */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getScoreColor(Math.round((opp.matchScore || 0) * 100))}`} />
                  <span className="text-xs font-medium text-gray-600">
                    {Math.round((opp.matchScore || 0) * 100)}% match
                  </span>
                </div>
                {opp.metadata?.comments ? (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <ChatBubbleLeftIcon className="h-3 w-3" />
                    {opp.metadata.comments}
                  </span>
                ) : null}
              </div>

              {/* Title */}
              <Link
                to={`/opportunities/${opp.opportunity?.samNoticeId || opp.opportunity?.id || opp.opportunityId}`}
                className="text-sm font-medium text-brand-600 hover:text-brand-800 line-clamp-2 mb-2 block"
              >
                {opp.opportunity?.title || 'Untitled Opportunity'}
              </Link>

              {/* Details */}
              <div className="space-y-1 text-xs text-gray-500">
                <p className="truncate">{opp.opportunity?.agency}</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                    {opp.opportunity?.naicsCode || 'N/A'}
                  </span>
                  {opp.opportunity?.setAside && (
                    <span className="text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                      {opp.opportunity?.setAside}
                    </span>
                  )}
                </div>
                <p className={`font-medium ${
                  timeLeft(opp.opportunity?.responseDeadline).includes('Expired') ? 'text-red-600' :
                  timeLeft(opp.opportunity?.responseDeadline).includes('today') ? 'text-orange-600' :
                  'text-green-600'
                }`}>
                  {timeLeft(opp.opportunity?.responseDeadline)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                {stage.id === 'new' && (
                  <button
                    onClick={() => handleAnalyze(opp.id)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <SparklesIcon className="h-3 w-3" />
                    Analyze
                  </button>
                )}
                {stage.id === 'qualified' && (
                  <button
                    onClick={() => handleMatchVendors(opp.id)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                  >
                    <UserCircleIcon className="h-3 w-3" />
                    Match
                  </button>
                )}
                <Link
                  to={`/opportunities/${opp.opportunity?.samNoticeId || opp.opportunity?.id || opp.opportunityId}`}
                  className="flex-1 text-center text-xs text-gray-600 hover:text-gray-800"
                >
                  View
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunity Pipeline</h1>
          <p className="text-sm text-gray-500">Track and manage opportunities through your workflow</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowConfigModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            Configure
          </button>
          <button
            onClick={fetchPipeline}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {PIPELINE_STAGES.map(stage => {
          const count = getStageOpportunities(stage.id).length;
          return (
            <div key={stage.id} className={`${stage.color} ${stage.borderColor} border rounded-lg p-3`}>
              <p className={`text-xs font-medium ${stage.textColor}`}>{stage.name}</p>
              <p className="text-2xl font-bold text-gray-900">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Min Match Score:</span>
          <input
            type="range"
            min="0"
            max="100"
            value={filter.minScore}
            onChange={(e) => setFilter(f => ({ ...f, minScore: parseInt(e.target.value) }))}
            className="flex-1 max-w-xs"
          />
          <span className="text-sm text-gray-600 w-12">{filter.minScore}%</span>
        </div>
      </div>

      {/* Kanban Board - 3 rows: 3-3-2 layout */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 space-y-4">
          {/* Row 1: New, Analyzing, Qualified */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PIPELINE_STAGES.slice(0, 3).map(stage => renderStageColumn(stage))}
          </div>
          {/* Row 2: Proposal, Submitted, Awarded */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PIPELINE_STAGES.slice(3, 6).map(stage => renderStageColumn(stage))}
          </div>
          {/* Row 3: Lost, Archived */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {PIPELINE_STAGES.slice(6, 8).map(stage => renderStageColumn(stage))}
          </div>
        </div>
      </div>

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Pipeline Configuration</h2>
              <button onClick={() => setShowConfigModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {config ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NAICS Codes</label>
                    <div className="flex flex-wrap gap-2">
                      {config.naicsCodes?.map((code: string) => (
                        <span key={code} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          {code}
                        </span>
                      )) || <span className="text-gray-500 text-sm">None configured</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agencies</label>
                    <div className="flex flex-wrap gap-2">
                      {config.agencies?.map((agency: string) => (
                        <span key={agency} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          {agency}
                        </span>
                      )) || <span className="text-gray-500 text-sm">None configured</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                    <div className="flex flex-wrap gap-2">
                      {config.keywords?.map((kw: string) => (
                        <span key={kw} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                          {kw}
                        </span>
                      )) || <span className="text-gray-500 text-sm">None configured</span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-gray-600">Auto Analyze</span>
                    <span className={`px-2 py-1 rounded text-sm ${config.autoAnalyze ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {config.autoAnalyze ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Auto Match Vendors</span>
                    <span className={`px-2 py-1 rounded text-sm ${config.autoMatchVendors ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {config.autoMatchVendors ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Loading configuration...</p>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
